import classNames from "classnames";
import React, { type Ref, useImperativeHandle, useRef } from "react";
import "./Flex.css";

export type Gap =
    | "value2"
    | "value4"
    | "value8"
    | "value12"
    | "value16"
    | "value20"
    | "value24"
    | "value28"
    | "value32"
    | "value36"
    | "value40"
    | "value44"
    | "value48";
export type AlignItems = "start" | "center" | "end" | "stretch";
export type AlignSelf = "start" | "center" | "end";
type JustifyContent =
    | "start"
    | "center"
    | "end"
    | "space-between"
    | "space-around"
    | "space-evenly";
type FlexWrap = "wrap" | "nowrap" | "wrap-reverse";

type FlexProps = {
    id?: string;
    className?: string;
    gap?: Gap;
    alignItems?: AlignItems;
    justifyContent?: JustifyContent;
    wrap?: FlexWrap;
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
    children: React.ReactNode;
    role?: string;
} & React.HTMLAttributes<HTMLDivElement>;

function gapCss(gap?: Gap) {
    return gap ? `web-lib__flex-gap--${gap}` : undefined;
}

function alignCss(alignItems: AlignItems) {
    return `web-lib__flex-align--${alignItems}`;
}

function alignSelfCss(alignSelf: AlignSelf) {
    return `web-lib__flex-align-self--${alignSelf}`;
}

function justifyCss(justifyContent?: JustifyContent) {
    return justifyContent ? `web-lib__flex-justify--${justifyContent}` : undefined;
}

function wrapCss(wrap?: FlexWrap) {
    return wrap ? `web-lib__flex-wrap--${wrap}` : undefined;
}

const FlexRow = React.forwardRef(
    (props: FlexProps, ref: Ref<HTMLDivElement | null>) => {
        const {
            children,
            className,
            gap,
            alignItems = "center",
            justifyContent,
            wrap,
            id,
            onClick,
            role,
            ...rest
        } = props;

        const divRef = useRef<HTMLDivElement>(null);
        useImperativeHandle(ref, () => {
            if (!divRef.current) {
                throw new Error("FlexRow ref is null - component not properly mounted");
            }

            return divRef.current;
        });

        return (
            <div
                id={id}
                onClick={onClick}
                role={role}
                className={classNames(
                    "web-lib__flex-row",
                    gapCss(gap),
                    alignCss(alignItems),
                    justifyCss(justifyContent),
                    wrapCss(wrap),
                    className,
                )}
                ref={divRef}
                {...rest}
            >
                {children}
            </div>
        );
    },
);

const FlexColumn = React.forwardRef(
    (props: FlexProps, ref: Ref<HTMLDivElement | null>) => {
        const {
            children,
            className,
            gap,
            alignItems = "start",
            justifyContent,
            wrap,
            id,
            onClick,
            role,
            ...rest
        } = props;

        const divRef = useRef<HTMLDivElement>(null);
        useImperativeHandle(ref, () => {
            if (!divRef.current) {
                throw new Error(
                    "FlexColumn ref is null - component not properly mounted",
                );
            }

            return divRef.current;
        });

        return (
            <div
                id={id}
                onClick={onClick}
                role={role}
                className={classNames(
                    "web-lib__flex-column",
                    gapCss(gap),
                    alignCss(alignItems),
                    justifyCss(justifyContent),
                    wrapCss(wrap),
                    className,
                )}
                ref={divRef}
                {...rest}
            >
                {children}
            </div>
        );
    },
);

type InlineFlexProps = {
    className?: string;
    gap?: Gap;
    alignSelf?: AlignSelf;
    wrap?: FlexWrap;
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
    children: React.ReactNode;
};

export function FlexInline(props: InlineFlexProps) {
    const { className, children, gap, alignSelf, wrap, onClick, ...rest } = props;

    return (
        <span
            className={classNames(
                "web-lib__flex-inline",
                className,
                gapCss(gap),
                alignCss("center"),
                alignSelfCss(alignSelf ?? "center"),
                wrapCss(wrap),
            )}
            onClick={onClick}
            {...rest}
        >
            {children}
        </span>
    );
}

export const Flex = {
    Row: FlexRow,
    Column: FlexColumn,
};
